import type { AgentType, LoadedChat } from '@ardoq/api-types';
import type {
  AgentInteractionRequested,
  AgentInteractionResolved,
} from '@ardoq/api';
import {
  ChatMessageDoneEvent,
  ChatMessageId,
  ChatMessageToolCallEvent,
  DecoratedSendMessageRequest,
  Feedback,
  type InteractionSubmissionState,
} from './types';
import { ClientRequest, UserEvent } from 'sync/types';
import omit from 'lodash/omit';
import { isCurrentChatMessageEvent } from './utils';
import {
  type AssistantConfig,
  type AssistantPreferences,
  DEFAULT_ASSISTANT_CONFIG,
} from '@ardoq/api-types';
import { AssistantMode, AssistantScreen } from './assistantTypes';
import { v4 as uuidv4 } from 'uuid';
import { GLOBAL_SIDE_PANEL_WIDTH } from '@ardoq/sidebar/consts';
import type { InteractionAnsweredPayload } from './actions';
import type {
  AssistantBanner,
  AssistantDisplayMode,
  AssistantDisplayModifier,
  DismissedBanner,
  NewAssistantBanner,
} from './assistantTypes';
import type { EphemeralNotificationKeys } from 'ephemeralNotification/types';
export { AssistantMode, AssistantScreen };
export type { AssistantDisplayMode, AssistantDisplayModifier };

type InteractionKind = AgentInteractionRequested['request']['kind'];
type TrackedInteraction = {
  kind: InteractionKind;
  status: 'open' | 'resolved';
};
export type ActiveInteractions = Readonly<Record<string, TrackedInteraction>>;

export type AssistantState = {
  mode: AssistantMode;
  screen: AssistantScreen;
  chatSessionId: string | null;
  sendMessageError: string | null;
  currentActiveRequest: string | null;
  editingPlanMessageId: ChatMessageId | null;
  currentAgentType: AgentType | undefined;
  cancelling: boolean;
  hasChatTimedOut: boolean;
  displayMode: AssistantDisplayMode;
  displayModifier: AssistantDisplayModifier;
  panelWidth: number;
  submittedFeedbacksByMessageId: Record<ChatMessageId, Feedback>;
  interactionSubmissionStateById: Record<string, InteractionSubmissionState>;
  activeToolDescription: string | null;
  activeToolName: string | null;
  executedCustomAgentIds: string[];
  webSearchToolUsed: boolean;
  showScrollDownButton: boolean;
  hasPythonAgentsFeature: boolean;
  hasInsightAgentFeature: boolean;
  hasCustomAgentsManagementAccess: boolean;
  autoScrollInLastMessage: boolean;
  currentScrollTop: number;
  input: string;
  animateDisplayModeHint: boolean;
  activeOnboardingHintKey: EphemeralNotificationKeys | null;
  hasPendingInputFocusRequest: boolean;
  banners: AssistantBanner[];
  isSnakeGameOpen: boolean;
  isRestoringLastOpenedSession: boolean;
  /** The interaction whose command this client has already run, if any. */
  handledInteractionId: string | null;
  /** Interactions of the active exchange. Resolved entries stay so replayed requests are ignored. */
  activeInteractions: ActiveInteractions;
  assistantConfig: AssistantConfig;
};

export const defaultAssistantState: AssistantState = {
  mode: AssistantMode.Closed,
  screen: AssistantScreen.Chat,
  chatSessionId: null,
  sendMessageError: null,
  currentActiveRequest: null,
  editingPlanMessageId: null,
  currentAgentType: undefined,
  cancelling: false,
  hasChatTimedOut: false,
  displayMode: 'sidePanel',
  displayModifier: 'default',
  panelWidth: GLOBAL_SIDE_PANEL_WIDTH,
  submittedFeedbacksByMessageId: {},
  interactionSubmissionStateById: {},
  activeToolDescription: null,
  activeToolName: null,
  executedCustomAgentIds: [],
  webSearchToolUsed: false,
  showScrollDownButton: false,
  hasPythonAgentsFeature: false,
  hasInsightAgentFeature: false,
  hasCustomAgentsManagementAccess: false,
  autoScrollInLastMessage: true,
  currentScrollTop: 0,
  input: '',
  animateDisplayModeHint: false,
  activeOnboardingHintKey: null,
  hasPendingInputFocusRequest: false,
  banners: [],
  isSnakeGameOpen: false,
  isRestoringLastOpenedSession: false,
  handledInteractionId: null,
  activeInteractions: {},
  assistantConfig: DEFAULT_ASSISTANT_CONFIG,
};

const persistedModeToAssistantMode = (
  mode: AssistantPreferences['mode']
): AssistantMode | undefined => {
  if (mode === 'open') return AssistantMode.Open;
  if (mode === 'closed') return AssistantMode.Closed;
  return undefined;
};

export const getInitialAssistantState = (
  assistantPrefs: AssistantPreferences | undefined
): AssistantState => ({
  ...defaultAssistantState,
  mode:
    persistedModeToAssistantMode(assistantPrefs?.mode) ??
    defaultAssistantState.mode,
  displayMode: assistantPrefs?.displayMode ?? defaultAssistantState.displayMode,
  panelWidth: assistantPrefs?.panelWidth ?? defaultAssistantState.panelWidth,
});

// --- Flags-only reducers ---

const handleInitializeNewChat = (state: AssistantState): AssistantState => ({
  ...state,
  mode: AssistantMode.Open,
  chatSessionId: null,
  sendMessageError: null,
  currentActiveRequest: null,
  editingPlanMessageId: null,
  cancelling: false,
  hasChatTimedOut: false,
  activeToolDescription: null,
  activeToolName: null,
  executedCustomAgentIds: [],
  webSearchToolUsed: false,
  activeOnboardingHintKey: null,
  banners: [],
  isSnakeGameOpen: false,
  isRestoringLastOpenedSession: false,
  handledInteractionId: null,
  interactionSubmissionStateById: {},
  activeInteractions: {},
});

const handleSetDisplayMode = (
  state: AssistantState,
  displayMode: AssistantDisplayMode
): AssistantState => ({
  ...state,
  displayMode,
  screen: displayMode === 'fullScreen' ? state.screen : AssistantScreen.Chat,
  animateDisplayModeHint: false,
  activeOnboardingHintKey: null,
});

const appendToInput = (currentInput: string, textToAppend: string): string => {
  const trimmedInput = currentInput.trim();
  if (!trimmedInput) return textToAppend;
  return `${trimmedInput}\n${textToAppend}`;
};

const handleAppendInput = (
  state: AssistantState,
  textToAppend: string
): AssistantState => ({
  ...state,
  input: appendToInput(state.input, textToAppend),
});

export const PLACEMENT_KEYS = ['mode', 'displayMode'] as const;

type AssistantPlacement = Pick<AssistantState, (typeof PLACEMENT_KEYS)[number]>;

const isDockedOpen = ({ mode, displayMode }: AssistantPlacement): boolean =>
  mode === AssistantMode.Open && displayMode === 'sidePanel';

const isFullscreenOpen = ({ mode, displayMode }: AssistantPlacement): boolean =>
  mode === AssistantMode.Open && displayMode === 'fullScreen';

const handleHideDockedChatWindow = (state: AssistantState): AssistantState =>
  state.mode === AssistantMode.Open
    ? { ...state, mode: AssistantMode.Minimized }
    : state;

const handleOpenChatWindow = (
  state: AssistantState,
  payload: { displayMode?: AssistantDisplayMode } | void
): AssistantState => {
  const withDisplayMode = (s: AssistantState): AssistantState =>
    payload?.displayMode ? handleSetDisplayMode(s, payload.displayMode) : s;
  if (state.mode === AssistantMode.Closed)
    return withDisplayMode(handleInitializeNewChat(state));
  if (state.mode === AssistantMode.Minimized)
    return withDisplayMode({ ...state, mode: AssistantMode.Open });
  return withDisplayMode(state);
};

const handleSendMessageFailed = (
  state: AssistantState,
  error: string
): AssistantState => ({
  ...state,
  sendMessageError: error,
  currentActiveRequest: null,
  handledInteractionId: null,
  interactionSubmissionStateById: {},
  activeInteractions: {},
});

const handleAgentRunWatchRestoreFailed = (
  state: AssistantState,
  { exchangeId }: { exchangeId: string }
): AssistantState =>
  state.currentActiveRequest === exchangeId
    ? { ...state, currentActiveRequest: null, activeInteractions: {} }
    : state;

const saveSubmittedFeedbackReducer = (
  state: AssistantState,
  { messageId, feedback }: { messageId: ChatMessageId; feedback: Feedback }
): AssistantState => ({
  ...state,
  submittedFeedbacksByMessageId: {
    ...state.submittedFeedbacksByMessageId,
    [messageId]: feedback,
  },
});

const validateInput = (value: string): boolean => !!value.trim();

const canSendMessage = ({
  input,
}: {
  input: string;
  documentCount: number;
}): boolean =>
  // TODO(AI-1435): temporarily disable document-only send until backend support is implemented.
  //   validateInput(input) || documentCount > 0
  validateInput(input);

// --- Cross-cutting reducers (flags slice) ---

const handleCloseChatWindow = (state: AssistantState): AssistantState => ({
  ...state,
  ...omit(defaultAssistantState, [
    'hasPythonAgentsFeature',
    'hasInsightAgentFeature',
    'hasCustomAgentsManagementAccess',
    'displayMode',
    'mode',
    'panelWidth',
  ]),
  mode: AssistantMode.Closed,
});

const handleSendMessage = (
  state: AssistantState,
  { clientRequestId, agentType }: DecoratedSendMessageRequest
): AssistantState => ({
  ...state,
  autoScrollInLastMessage: true,
  currentActiveRequest: clientRequestId,
  editingPlanMessageId: null,
  currentAgentType: agentType,
  hasChatTimedOut: false,
  activeInteractions: {},
  executedCustomAgentIds: [],
  webSearchToolUsed: false,
  input: '',
  banners: [],
  activeOnboardingHintKey: null,
  isRestoringLastOpenedSession: false,
});

const setIsCurrentChatMessageDone = (
  state: AssistantState,
  event: UserEvent<ClientRequest<ChatMessageDoneEvent>>
): AssistantState => {
  if (!isCurrentChatMessageEvent(state.currentActiveRequest, event))
    return state;

  if (event.data.cancelled) {
    return {
      ...state,
      currentActiveRequest: null,
      cancelling: false,
      sendMessageError: null,
      hasChatTimedOut: false,
      activeToolDescription: null,
      activeToolName: null,
      handledInteractionId: null,
      interactionSubmissionStateById: {},
      activeInteractions: {},
    };
  }

  return {
    ...state,
    cancelling: false,
    currentActiveRequest: null,
    activeToolDescription: null,
    activeToolName: null,
    handledInteractionId: null,
    interactionSubmissionStateById: {},
    activeInteractions: {},
  };
};

// Marker stored when the websocket error event carries no message of its own.
// The view layer (viewModel$.ts) substitutes a localized string at render time
// because reducers have no access to `i18nLocale$`.
export const DEFAULT_SEND_MESSAGE_ERROR_MARKER =
  '__assistant_default_send_message_error__';

const EXECUTE_CUSTOM_AGENT_TOOL_NAME = 'execute_custom_agent';
const WEB_SEARCH_TOOL_NAME = 'search_web';

type ToolCallInput = ChatMessageToolCallEvent['input'];

const parseToolCallInput = (
  input: ToolCallInput
): Record<string, unknown> | null => {
  if (!input) return null;
  if (typeof input !== 'string') return input;
  try {
    return JSON.parse(input) as Record<string, unknown> | null;
  } catch {
    return null;
  }
};

const parseCustomAgentId = (input: ToolCallInput): string | null => {
  const agentId = parseToolCallInput(input)?.agent_id;
  return typeof agentId === 'string' ? agentId : null;
};

const applyToolCall = (
  state: AssistantState,
  toolCall: ChatMessageToolCallEvent
): AssistantState => {
  const base: AssistantState = {
    ...state,
    activeToolDescription: toolCall.action_description ?? null,
    activeToolName: toolCall.name ?? null,
  };

  if (toolCall.name === EXECUTE_CUSTOM_AGENT_TOOL_NAME) {
    const agentId = parseCustomAgentId(toolCall.input);
    if (agentId) {
      return {
        ...base,
        executedCustomAgentIds: [...state.executedCustomAgentIds, agentId],
      };
    }
  }

  if (toolCall.name === WEB_SEARCH_TOOL_NAME) {
    return {
      ...base,
      webSearchToolUsed: true,
    };
  }

  return base;
};

const setActiveToolDescription = (
  state: AssistantState,
  event: UserEvent<ClientRequest<ChatMessageToolCallEvent>>
): AssistantState => {
  if (!isCurrentChatMessageEvent(state.currentActiveRequest, event))
    return state;
  return applyToolCall(state, event.data);
};

const setSendMessageError = (
  state: AssistantState,
  { message }: { message: string }
): AssistantState => ({
  ...state,
  sendMessageError: message || DEFAULT_SEND_MESSAGE_ERROR_MARKER,
  currentActiveRequest: null,
  handledInteractionId: null,
  interactionSubmissionStateById: {},
  activeInteractions: {},
});

const handleHistoricChatLoaded = (
  state: AssistantState,
  payload: LoadedChat
): AssistantState => ({
  ...state,
  chatSessionId: payload._id,
  screen:
    state.displayMode === 'fullScreen' ? state.screen : AssistantScreen.Chat,
  submittedFeedbacksByMessageId: {},
  currentActiveRequest: null,
  editingPlanMessageId: null,
  interactionSubmissionStateById: {},
  activeToolDescription: null,
  activeToolName: null,
  handledInteractionId: null,
  activeInteractions: {},
  executedCustomAgentIds: [],
  webSearchToolUsed: false,
  showScrollDownButton: false,
  activeOnboardingHintKey: null,
  banners: [],
  isSnakeGameOpen: false,
});

const exitFullScreenOnNavigation = (state: AssistantState): AssistantState => {
  if (state.displayMode !== 'fullScreen') return state;
  return { ...state, displayMode: 'sidePanel', screen: AssistantScreen.Chat };
};

const handleAddBanner = (
  state: AssistantState,
  banner: NewAssistantBanner
): AssistantState => {
  const isAlreadyShown = state.banners.some(
    ({ message }) => message === banner.message
  );
  if (isAlreadyShown) return state;
  return { ...state, banners: [...state.banners, { ...banner, id: uuidv4() }] };
};

const handleDismissBanner = (
  state: AssistantState,
  { bannerId }: DismissedBanner
): AssistantState => ({
  ...state,
  banners: state.banners.filter(({ id }) => id !== bannerId),
});

const setHandledInteractionId = (
  state: AssistantState,
  handledInteractionId: string
): AssistantState => ({ ...state, handledInteractionId });

const trackInteractionRequested = (
  state: AssistantState,
  { exchangeId, interactionId, request }: AgentInteractionRequested
): AssistantState => {
  if (exchangeId !== state.currentActiveRequest) return state;
  if (state.activeInteractions[interactionId]) return state;
  return {
    ...state,
    activeInteractions: {
      ...state.activeInteractions,
      [interactionId]: { kind: request.kind, status: 'open' },
    },
  };
};

const trackInteractionResolved = (
  activeInteractions: ActiveInteractions,
  interactionId: string
): ActiveInteractions => {
  const interaction = activeInteractions[interactionId];
  if (!interaction) return activeInteractions;
  return {
    ...activeInteractions,
    [interactionId]: { ...interaction, status: 'resolved' },
  };
};

const dropIfNotRetryable = (
  activeInteractions: ActiveInteractions,
  interactionId: string
): ActiveInteractions => {
  const interaction = activeInteractions[interactionId];
  if (!interaction) return activeInteractions;

  const canRetryAfterFailure = interaction.kind !== 'ui_command';
  if (canRetryAfterFailure) return activeInteractions;

  return omit(activeInteractions, interactionId);
};

const hasOpenInteraction = ({ activeInteractions }: AssistantState): boolean =>
  Object.values(activeInteractions).some(({ status }) => status === 'open');

const handleInteractionResolved = (
  state: AssistantState,
  { interactionId }: AgentInteractionResolved
): AssistantState => ({
  ...state,
  handledInteractionId: interactionId,
  interactionSubmissionStateById: omit(
    state.interactionSubmissionStateById,
    interactionId
  ),
  activeInteractions: trackInteractionResolved(
    state.activeInteractions,
    interactionId
  ),
});

const handleInteractionSubmitting = (
  state: AssistantState,
  { interactionId }: InteractionAnsweredPayload
): AssistantState => ({
  ...state,
  interactionSubmissionStateById: {
    ...state.interactionSubmissionStateById,
    [interactionId]: 'submitting',
  },
});

const handleInteractionSubmissionFailed = (
  state: AssistantState,
  interactionId: string
): AssistantState => ({
  ...state,
  handledInteractionId: null,
  interactionSubmissionStateById: {
    ...state.interactionSubmissionStateById,
    [interactionId]: 'failed',
  },
  activeInteractions: dropIfNotRetryable(
    state.activeInteractions,
    interactionId
  ),
});

const isInteractionHandled = (
  { handledInteractionId }: AssistantState,
  interactionId: string
): boolean => handledInteractionId === interactionId;

/**
 * Whether a display mode belongs in the user's saved preferences. `fullScreen`
 * is a transient view the app leaves on navigation, and `standalone` belongs to
 * the assistant-only entry — persisting either would change what the app opens
 * with next time.
 */
const isPersistedDisplayMode = (
  displayMode: AssistantDisplayMode
): displayMode is NonNullable<AssistantPreferences['displayMode']> =>
  displayMode !== 'fullScreen' && displayMode !== 'standalone';

export const assistantStateOperations = {
  applyToolCall,
  isPersistedDisplayMode,
  handleOpenChatWindow,
  handleHideDockedChatWindow,
  isDockedOpen,
  isFullscreenOpen,
  handleInitializeNewChat,
  handleSendMessageFailed,
  saveSubmittedFeedbackReducer,
  validateInput,
  canSendMessage,
  handleCloseChatWindow,
  handleSendMessage,
  handleAgentRunWatchRestoreFailed,
  setActiveToolDescription,
  setIsCurrentChatMessageDone,
  setSendMessageError,
  handleHistoricChatLoaded,
  exitFullScreenOnNavigation,
  handleSetDisplayMode,
  handleAppendInput,
  handleAddBanner,
  handleDismissBanner,
  setHandledInteractionId,
  trackInteractionRequested,
  hasOpenInteraction,
  handleInteractionResolved,
  handleInteractionSubmitting,
  handleInteractionSubmissionFailed,
  isInteractionHandled,
};
